<!-- Phase 3 create-modal template: BUI-styled form content hosted inside the
     shared Dialog.vue wrapper (see PlayersListView.vue). -->

<template>
  <div class="create-player-form">
    <StatusBar
      :errorMessage="errorMessage"
      :isLoading="isLoading"
      loadingMessage="Saving..."
      :isSuccess="isSuccess"
      successMessage="Saved!"
      errorTestId="registration-error"
    />
    <p class="cpf-hint">To register a player, enter the registration code shown on the device in the field below.
      The code is 6 digits consisting of letters and numbers. It is not case sentistive.</p>
    <div class="field-content">
      <!-- TODO: think about adding below-field error message & error state indication (e.g. red border) [DSP-N26CxI4P] -->
      <div class="d-flex field-row">
        <label for="cpf-code" class="basis-1/2 text-sm font-bold">Registration Code &#9733;</label>
        <TextField id="cpf-code" data-test-id="player-code" v-model="values.code" class="basis-1/2" width="100%" />
      </div>
      <div class="d-flex field-row">
        <label class="basis-1/2 text-sm font-bold">Location &#9733;</label>
        <div class="basis-1/2 cpf-location">
          <span v-if="values.location?.name" class="cpf-location-name">{{ values.location?.name }}</span>
          <SelectLocationDialog @save="addLocation($event)" :showBtn="values.location?.name ? false : true" />
        </div>
      </div>
      <div>
        <SyncSplitConfig v-model="values.syncConfig" />
      </div>
    </div>
    <div class="form-buttons">
      <ButtonCustomized label="Cancel" data-testid="player-cancel" @click="onCancel" ctaSecondary />
      <ButtonCustomized label="Register" data-testid="player-register" cta
        @click="onSubmit"
        :disabled="!values.code || !values.location"
        :loading="isLoading"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import TextField from "@/components/form-fields/TextField.vue";
import useCreatePlayerForm from "./CreatePlayerFormHelper";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import SyncSplitConfig from "@/views/PlayerEditorView/SyncSplitConfig/SyncSplitConfig.vue";
import useNotifications from "@/components/Notifications/composables/notifications";
import router from '@/router';
import SelectLocationDialog from "./SelectLocationDialog.vue";

const emit = defineEmits(['submit', 'cancel', 'success', 'fail'])

const notification = useNotifications();

const { values, submitForm, isLoading, isSuccess, errorMessage, newPlayerId } = useCreatePlayerForm();

async function onSubmit() {
  await submitForm();
  if(isSuccess.value === true) {
    notification.success('Player created successfully!');
    emit('success');
    router.push({name: 'player-editor', params: {id: newPlayerId.value}});
  } else {    // on failure
    emit('fail');
  }
}
function addLocation(selectedLocation: any) {
  values.location = selectedLocation
}
function onCancel() {
  emit('cancel');
}
</script>

<style lang="scss" scoped>
.create-player-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: min(26rem, calc(100vw - 6rem));
  color: var(--bui-tbl-row-text, #2a2969);
}

.cpf-hint {
  margin: 0;
  font-size: 0.875rem;
  color: var(--bui-tbl-detail-text, #5c5c66);
}

.field-content {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;

  :deep(.field label) {
    color: var(--bui-tbl-row-text, #2a2a30);
  }

  :deep(.field input[type='text']) {
    color: var(--bui-ter-input-text, #1a1a1e);
    background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    width: 100%;
  }
}

.field-row {
  align-items: center;

  label {
    color: var(--bui-tbl-row-text, #2a2a30);
  }
}

.cpf-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  /* Dialog expander wrapper hugs its button/link. */
  :deep(> .flex),
  :deep(.d-inline) {
    display: inline-flex;
    width: auto;
    flex: 0 0 auto;
    justify-content: flex-start;
  }
}

.cpf-location-name {
  font-size: 0.875rem;
  font-weight: 600;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}
</style>
